<template>
  <div class="campaign-card">
    <div class="header">
      <img
        :src="imageSrc"
        alt="Campaign header"
      /><input
        v-if="props.mode !== 'preview'"
        type="file"
        id="image"
        accept="image/png, image/jpeg, image/gif"
        @change="previewImage"
        ref="uploadInput"
      />
      <label  v-if="props.mode !== 'preview'" for="image" class="btn secondary no-dark-mode camp-toogle-theme-bt">
        <i class="bi bi-pencil " />
      </label>
    </div>
    <div class="body">
      <div v-if="props.mode == 'preview'">
        <div class="name-duration-row">
          <div class="block">
            <h4 class="card-title" v-text="data.public_name" />
            <p class="job" v-text="data.job_title" />
          </div>
          <div class="informations-container">
            <div class="tag-container">
              <div class="tag" :style="getThemeStyle">TEMPS MOYEN: {{ duration }} MIN</div>
            </div>
          </div>
        </div>
        <p class="campaign-description" v-html="data.job_description" />
      </div>      
      <div v-else>
        <div class="name-duration-row">
          <VField
            name="campaign-public-name"
            placeholder="Nom de l'organisation"
            :class="{
              invalid:
                error?.field === 'public_name' || data.public_name?.length > 50,
            }"
            :modelValue="data.public_name"
            @input="(event) => emits('changePublicName', event.target.value)"
          />
          <div class="informations-container">
            <div class="tag-container">
              <div class="tag" :style="getThemeStyle">TEMPS MOYEN: {{ duration }} MIN</div>
            </div>
          </div>
        </div>
        <VField
          name="campaing-public-job"
          placeholder="Contexte (ex : Manager F/H - CDI)"
          :class="{
            invalid: error?.field === 'job_title' || data.job_title?.length > 50,
          }"
          :modelValue="data.job_title"
          @input="(event) => emits('changeJobTitle', event.target.value)"
        />
        <br>
      <QuillEditor
        class="quill-editor"
        id="campaign-public-description"
        v-model:content="content" contentType="html"
        :options="quillOptions"
        />
        <p
          v-if="error"
          class="red-color bold"
          v-text="getError(error.name, error.params)"
        />
      </div>
    <Button
      class="no-dark-mode campaign-action-button"
      :style="getThemeStyle"
      label="Démarrer l’aventure maintenant !"
    />
    </div>
    <div class="tooltip" v-if="showTooltip">
      <div>Nous vous recommandons de faire apparaître votre logo dans cet encart.</div>
    </div>     
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch  } from "vue";
import { useToast } from "vue-toastification";
import { getError } from "@/composables/error.js";
import { getImage, getRandomCampaignPicture } from "@/composables/getImage.js";

const quillOptions = {
      theme: 'snow',      
      placeholder: 'Décrivez succinctement votre campagne.',
      modules: {
        toolbar: [['bold', 'italic', 'underline'], ['link']],
      }
    };

var content = ref('');

// Ref for the image src
const imageSrc = ref('');

const showTooltip = ref(false);

onMounted(() => {
  content.value = props.data.job_description  ? props.data.job_description : '';
});

const handleContentChange = (newValue) => {
  props.data.job_description = newValue;
  emits('changeJobDescription', newValue);
};

watch(content, handleContentChange);

const emits = defineEmits([
  "changeImage",
  "changePublicName",
  "changeJobTitle",
  "changeJobDescription",
  "toggleError",
]);

const toast = useToast();

const uploadInput = ref(null);

var props = defineProps({
  data: {
    type: Object,
  },
  mode:{
    type: String,
  },
  text_colour:{
    type: String,
  },
  elem_colour:{
    type: String,
  },
});

var imageChange = false;
showTooltip.value = props.mode == "new";

watch(() => props.mode, (newValue, oldValue) => {
 // React to prop changes
 if(!imageChange) {
  showTooltip.value = props.mode == "new";
 }
});

const getThemeStyle = computed(() => {

  var bgColour = "#f2cb36";
  var textColour = "#000000";

  return {
  'background-color': props.elem_colour ? props.elem_colour : bgColour,
  'color': props.text_colour ? props.text_colour : textColour,
  };
});
  
var imageWatcherEnabled = true;

watch(() => props.data.image_id,
  (newImageId, oldValue) => {
    if(imageWatcherEnabled) {
      if (newImageId) {
        imageSrc.value = getImage(newImageId);
        
        // Reset the file input element so that any new file can be uploaded, even the same one
        if(uploadInput.value) {
          uploadInput.value.value = null; // This ensures the input is reset if needed
        }
        
        showTooltip.value = false;
        imageChange = true;
      } else {
        //put a generic image again
        imageSrc.value = getRandomCampaignPicture(props.data.name);
        imageChange = false;
      }
    }
  },
  { immediate: true, deep: true } // This makes it run on initial load as well
);

// Initial setting of imageSrc
if (props.data.image_id) {
  imageSrc.value = getImage(props.data.image_id);
} else {
  imageSrc.value = getRandomCampaignPicture(props.data.name);
}

function checkProperty(property, max, field) {
  if (property === "") {
    emits("toggleError", {
      name: "ERROR_REQUIRED_FIELD",
    });

    return {
      name: "ERROR_REQUIRED_FIELD",
      field,
    };
  }

  if (property?.length > max) {
    emits("toggleError", {
      name: "ERROR_TOO_LONG",
      params: { size: max },
    });

    return {
      name: "ERROR_TOO_LONG",
      params: { size: max },
      field,
    };
  }

  return null;
}

const error = computed(() => {
  if (checkProperty(props.data.public_name, 50, "public_name"))
    return checkProperty(props.data.public_name, 50, "public_name");

  if (checkProperty(props.data.job_title, 50, "job_title"))
    return checkProperty(props.data.job_title, 50, "job_title");

  emits("toggleError", null);

  return null;
});

async function previewImage() {
  const [file] = uploadInput.value.files;

  const maxSize = 1024 * 2048; // 2Mo

  if (!["image/png", "image/jpeg", "image/gif"].includes(file.type)) {
    toast.error("Format de fichier non autorisé");
    return;
  }

  if (file.size > maxSize) {
    toast.error(
      "Le poids de l'image dépasse 2Mo, veuillez ressayer avec une image plus légère."
    );
    return;
  }

  imageSrc.value = URL.createObjectURL(file);
  showTooltip.value = false;

  imageChange = true;
  imageWatcherEnabled = false; //disable image changing watcher as image changed in component

  emits("changeImage", file);
  setTimeout(() => {  imageWatcherEnabled = true;  }, 200); //enable it again
  // Reset the file input element to allow re-selecting the same file
  uploadInput.value.value = null;
}

const duration = computed<number>(() => {
  const version = getVersionObjectFromVersionID(props.data.calibration_id);
  if (version) return version.duration;
  return 50;
});
</script>

<style>
.ql-toolbar{
  border: 1px solid #464646!important;
  border-radius: 6px 6px 0px 0px;
}
.ql-editor{
  min-height: 130px;
  max-height: 130px;
  padding-top: 0px!important;
  font-size: 16px!important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
  line-height: 1.3!important;
}
.ql-editor.ql-blank::before {
  padding-top: 12px!important;
  color: white!important;
}
.ql-container {
  border: 1px solid #464646!important;
  border-radius: 0px 0px 6px 6px;
}
.ql-tooltip {
  background:#1D1D1D!important; 
  color: white!important;
}
.ql-snow .ql-stroke {
  stroke: white!important;
}

.quill-editor input[type=text],
.quill-editor input[type=number],
.quill-editor input[type=email],
.quill-editor input[type=tel],
.quill-editor input[type=password] {
  color: white !important;
}

/* Change the color on hover */
.ql-toolbar .ql-formats button:hover {
  background-color: #464646!important; 
}
.ql-snow.ql-toolbar button.ql-active {
  background-color: #464646!important; 
}

</style>
